import * as React from 'react';
import { TrackedPropertyRelationship } from '../../graphql.generated';

export type Dispatch = (action: Action) => void;
type Action = {
    type:
        | 'trackProperty'
        | 'untrackProperty'
        | 'error'
        | 'saveRelationship'
        | 'allowConsent'
        | 'revokeConsent';
    relationship?: TrackedPropertyRelationship;
};
export type State = {
    isTracked: boolean;
    relationship?: TrackedPropertyRelationship;
    hasPermissions: boolean;
};

const TrackPropertyContext = React.createContext<
    { state: State; dispatch: Dispatch } | undefined
>(undefined);
TrackPropertyContext.displayName = 'TrackPropertyContext';

function useTrackPropertyContext() {
    const context = React.useContext(TrackPropertyContext);
    if (context === undefined) {
        throw new Error(
            'useTrackPropertyContext must be used within a TrackPropertyProvider'
        );
    }
    return context;
}

function trackPropertyReducer(state: State, action: Action): State {
    switch (action.type) {
        case 'trackProperty': {
            return {
                isTracked: true,
                relationship: undefined,
                hasPermissions: true
            };
        }
        case 'untrackProperty': {
            return {
                isTracked: false,
                relationship: undefined,
                hasPermissions: false
            };
        }
        case 'error': {
            return {
                isTracked: state.isTracked,
                relationship: undefined,
                hasPermissions: state.hasPermissions
            };
        }
        case 'saveRelationship': {
            return {
                isTracked: true,
                relationship: action.relationship,
                hasPermissions: state.hasPermissions
            };
        }
        case 'allowConsent': {
            return {
                isTracked: true,
                relationship: state.relationship,
                hasPermissions: true
            };
        }
        case 'revokeConsent': {
            return {
                isTracked: state.isTracked,
                relationship: state.relationship,
                hasPermissions: false
            };
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}

type TrackPropertyProviderProps = {
    children: React.ReactNode;
    initialState: State;
};
function TrackPropertyProvider({
    children,
    initialState
}: TrackPropertyProviderProps) {
    const [state, dispatch] = React.useReducer(
        trackPropertyReducer,
        initialState
    );
    return (
        <TrackPropertyContext.Provider value={{ state, dispatch }}>
            {children}
        </TrackPropertyContext.Provider>
    );
}

export { TrackPropertyProvider, useTrackPropertyContext };
